import { useCallback } from 'react'

import { Checkbox } from '@vtex/shoreline'

import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import styles from '../styles.module.css'
import { useRowSelection } from '../useRowSelection'
import { CandidateListItem } from './CandidateListItem'
import { ProductCandidate, ProductSelection } from './types'

const getKey = (row: ProductCandidate) => row.key

type CandidateListProps = {
  rows: ProductCandidate[]
  label: string
  selected: ProductSelection
  onSelectedChange: (next: ProductSelection) => void
  /** Chave do produto → marca a que já está vinculado (`null` quando está sem marca). */
  existingByKey: Record<string, string | null>
  /** Conteúdo à direita da linha; não é chamado para produto já vinculado. */
  renderAside?: (row: ProductCandidate) => React.ReactNode
}

/**
 * Lista de candidatos com "selecionar todos" no cabeçalho. Produto já em
 * "Meus produtos" aparece marcado e travado, com a marca a que pertence.
 */
export const CandidateList: React.FC<CandidateListProps> = ({
  rows,
  label,
  selected,
  onSelectedChange,
  existingByKey,
  renderAside
}) => {
  const { t } = useAPPTranslation()

  // `useRowSelection` refiltra as linhas quando esta função muda de identidade
  const isDisabled = useCallback(
    (key: string) => key in existingByKey,
    [existingByKey]
  )

  const selection = useRowSelection(rows, getKey, {
    selected,
    onChange: onSelectedChange,
    isDisabled
  })

  return (
    <>
      <div className={styles.suggestionListHeader}>
        <Checkbox
          checked={selection.allSelected}
          indeterminate={!selection.allSelected && selection.someSelected}
          disabled={!rows.some(row => !(row.key in existingByKey))}
          onChange={selection.toggleAll}
          aria-label={t('rm:advertiserCatalog.selectAll')}
        />
        {/* sem linhas, o cabeçalho mostra os nomes das colunas */}
        <span className={styles.count}>
          {rows.length ? label : t('rm:advertiserProducts.table.column.gtin')}
        </span>
        {!!renderAside && (
          <span className={`${styles.count} ${styles.rowBrand}`}>
            {t('rm:advertiserProducts.table.column.brand')}
          </span>
        )}
      </div>

      <ul className={styles.suggestionList}>
        {rows.map(row => (
          <CandidateListItem
            key={row.key}
            row={row}
            existingByKey={existingByKey}
            isSelected={selection.isSelected(row.key)}
            toggle={selection.toggle}
            renderAside={renderAside}
          />
        ))}
      </ul>
    </>
  )
}
